import {
    ChangeDetectionStrategy,
    Component,
    afterNextRender,
    computed,
    inject,
    linkedSignal,
    resource,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NavigationService } from '@animus/utility/web/navigation';
import { HORDE_SESSION_PERSISTENCE } from '@animus/utility/web/persistence';
import { OfflineReadinessStore } from '@animus/utility/web/service-worker';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
    lucideCircleCheck,
    lucideCircleX,
    lucideCloudOff,
    lucideDices,
    lucideFlag,
    lucideHome,
    lucideInfo,
    lucideMenu,
    lucideMoon,
    lucidePackage,
    lucideRefreshCw,
    lucideScrollText,
    lucideSettings,
    lucideSun,
    lucideX,
} from '@ng-icons/lucide';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { map } from 'rxjs';
import { DISCLAIMER_ACK_KEY, DisclaimerDialog } from './disclaimer-dialog';
import { LicencesRightsDialog } from './licences-rights-dialog';
import { OfflineStatusDialog } from './offline-status-dialog';
import { ThemeService } from './theme.service';
import { UpdateAvailableDialog } from './update-available-dialog';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, RouterLink, RouterLinkActive, NgIcon, HlmSheetImports],
    providers: [
        provideIcons({
            lucideCircleCheck,
            lucideCircleX,
            lucideCloudOff,
            lucideDices,
            lucideFlag,
            lucideHome,
            lucideInfo,
            lucideMenu,
            lucideMoon,
            lucidePackage,
            lucideRefreshCw,
            lucideSettings,
            lucideScrollText,
            lucideSun,
            lucideX,
        }),
    ],
    selector: 'app-root',
    templateUrl: './app.html',
})
export class App {
    protected readonly theme = inject(ThemeService);

    protected readonly offlineReadiness = inject(OfflineReadinessStore);

    private readonly router = inject(Router);

    private readonly nav = inject(NavigationService);

    private readonly dialog = inject(HlmDialogService);

    private readonly persistence = inject(HORDE_SESSION_PERSISTENCE);

    /**
     * Probes storage by reading the session index. Modelled as a resource so a denied/quota-exhausted database
     * settles into `error()` instead of an unhandled rejection, and "Retry storage" is a plain `reload()`.
     */
    private readonly storageProbe = resource({
        loader: () => this.persistence.listPublicSessions(),
    });

    protected readonly storageStatus = computed<'connecting' | 'ready' | 'unavailable'>(() => {
        if (this.storageProbe.isLoading()) {
            return 'connecting';
        }

        return this.storageProbe.error() === undefined ? 'ready' : 'unavailable';
    });

    /** About-page banner under the nav bar; hidden once dismissed, and that dismissal is persisted. */
    protected readonly aboutBannerVisible = linkedSignal(() => {
        try {
            return localStorage.getItem('horde-about-banner-dismissed') !== 'true';
        } catch {
            return true;
        }
    });

    protected dismissAboutBanner(): void {
        try {
            localStorage.setItem('horde-about-banner-dismissed', 'true');
        } catch {
            // LocalStorage unavailable - banner stays dismissed for the session only.
        }

        this.aboutBannerVisible.set(false);
    }

    constructor() {
        // Alpha disclaimer: show once per browser unless the user asked not to see it again. Runs after first
        // render so the dialog never blocks the initial paint, and only in the browser.
        afterNextRender(() => {
            try {
                if (localStorage.getItem(DISCLAIMER_ACK_KEY) === 'accepted') {
                    return;
                }
            } catch {
                // LocalStorage unavailable - still show the disclaimer so the user is informed.
            }

            this.dialog.open(DisclaimerDialog, {
                // Clicking the backdrop dismisses the disclaimer too (the original TODO requirement), which
                // spartan-ng's dialog disables by default.
                closeOnOutsidePointerEvents: true,
            });
        });
    }

    protected retryStorage(): void {
        this.storageProbe.reload();
    }

    /** Exports the current RxDB-localstorage bytes without requiring the database to open successfully. */
    protected exportStoredData(): void {
        const records: Record<string, string | null> = {};

        for (let index = 0; index < localStorage.length; index += 1) {
            const key = localStorage.key(index);

            if (key?.startsWith('RxDB-ls-') && key.includes('-horde-v4--')) {
                records[key] = localStorage.getItem(key);
            }
        }

        const url = URL.createObjectURL(
            new Blob([JSON.stringify({ database: 'horde-v4', records }, null, 2)], {
                type: 'application/json',
            })
        );
        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = `horde-storage-export-${new Date().toISOString()}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    protected readonly navItems = [
        { label: 'Home', path: '/' },
        { label: 'Rules', path: '/library/rules' },
        { label: 'Factions', path: '/library/factions' },
        { label: 'Custom Brackets', path: '/library/custom-spawn' },
        { label: 'Resupply Table', path: '/library/resupply-table' },
        { label: 'About', path: '/about' },
        { label: 'Settings', path: '/settings' },
    ] as const;

    private readonly currentUrl = toSignal(injectNavigationEnd().pipe(map(() => this.router.url)), {
        initialValue: typeof location === 'undefined' ? '/' : location.pathname,
    });

    protected readonly currentNav = computed(() => {
        const url = this.currentUrl();

        return this.navItems.find((item) => url === item.path || url.startsWith(`${item.path}/`))?.path ?? '';
    });

    protected navTo(path: string): void {
        if (path) {
            this.nav.go({ commands: [path] });
        }
    }

    protected openLicencesAndRights(): void {
        this.dialog.open(LicencesRightsDialog, {
            contentClass: 'sm:max-w-lg',
        });
    }

    protected openOfflineStatus(): void {
        this.dialog.open(OfflineStatusDialog, {
            contentClass: 'sm:max-w-md',
        });
    }

    protected openUpdateAvailable(): void {
        this.dialog.open(UpdateAvailableDialog, {
            contentClass: 'sm:max-w-md',
        });
    }
}
